/**
 * Typed date entry — parsing and display for `GaiaDateField`.
 *
 * Form state holds an ISO calendar date (`"YYYY-MM-DD"`, no time, no zone);
 * these helpers translate between that and what a person actually types.
 *
 * The one rule worth knowing: an all-numeric date is read DAY FIRST, so
 * `3/2/2026` is 3 February. Both the live echo under the field and the value
 * the field settles on spell the month as a word ("03 Feb 2026"), so a
 * misread is visible the moment it happens rather than at signing time.
 *
 * Month and weekday names, and the validity of a day, come from dayjs — the
 * wording is the same on every machine (unlike `toLocaleDateString`) without
 * a list to keep by hand.
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(customParseFormat);
dayjs.extend(localeData);

/** The canonical wire format: a calendar date with no time and no zone. */
const ISO_FORMAT = 'YYYY-MM-DD';

/** Relative words worth typing — the rest of the vocabulary is a real date. */
const RELATIVE_DAYS: Record<string, number | undefined> = {
  today: 0,
  tomorrow: 1,
  yesterday: -1,
};

export interface ParsedDateInput {
  /** Canonical `YYYY-MM-DD`. */
  iso: string;
  /** The same day as a local `Date` (midnight), for the calendar panel. */
  date: Date;
}

/** Strict read of `"YYYY-MM-DD"`; invalid for anything that isn't one. */
function fromIso(iso: string): dayjs.Dayjs {
  return dayjs(iso.trim(), ISO_FORMAT, true);
}

/** `"YYYY-MM-DD"` → local midnight `Date`; null when the string isn't one. */
export function dateFromIso(iso: string): Date | null {
  const parsed = fromIso(iso);
  return parsed.isValid() ? parsed.toDate() : null;
}

/** Local `Date` → `"YYYY-MM-DD"` (never `toISOString()`, which shifts zones). */
export function isoFromDate(date: Date): string {
  return dayjs(date).format(ISO_FORMAT);
}

/**
 * The settled form of a date in an input: `"03 Feb 2026"`. A written month
 * is the whole point — a person reading it back can't take the day for the
 * month, whatever they typed to get here.
 */
export function formatDateInputDisplay(iso: string): string {
  const parsed = fromIso(iso);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : '';
}

/**
 * The long echo shown while typing: `"Tuesday, 3 February 2026"`. The weekday
 * earns its place — an effective date landing on a Sunday is usually a typo.
 */
export function describeDateInput(iso: string): string {
  const parsed = fromIso(iso);
  return parsed.isValid() ? parsed.format('dddd, D MMMM YYYY') : '';
}

/** Reject anything that isn't a real calendar day (31 Feb, month 13, …). */
function buildDate(year: number, month: number, day: number): ParsedDateInput | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  // Strict parsing does the calendar work: a rolled-over date (31 Feb → 3 Mar),
  // a month past 12 or a year under four digits never comes back valid.
  const parsed = dayjs(`${year}-${month}-${day}`, 'YYYY-M-D', true);
  if (!parsed.isValid()) return null;
  return { iso: parsed.format(ISO_FORMAT), date: parsed.toDate() };
}

/** `26` → 2026, `98` → 1998. Four-digit years pass through untouched. */
function expandYear(raw: string): number {
  const value = Number(raw);
  if (raw.length > 2) return value;
  return value < 70 ? 2000 + value : 1900 + value;
}

/**
 * A month from its name or a prefix of it, three letters minimum — fewer
 * can't tell March from May, or June from July. "Sept" is spelled out often
 * enough to be worth its own line.
 */
function monthFromName(token: string): number | null {
  const name = token.replace(/\.$/, '');
  if (name.length < 3) return null;
  if (name === 'sept') return 9;
  const index = dayjs.months().findIndex(month => month.toLowerCase().startsWith(name));
  return index === -1 ? null : index + 1;
}

/**
 * Day-first reading of two numbers that could each be a day or a month. When
 * only one arrangement is a real date (13/2 can only be 13 February) that one
 * wins; when both work (3/2), the first number is the day.
 */
function orderDayMonth(first: number, second: number): { day: number; month: number } {
  if (first > 12 && second <= 12) return { day: first, month: second };
  if (second > 12 && first <= 12) return { day: second, month: first };
  return { day: first, month: second };
}

/**
 * Read a typed date. Accepts, in day-first reading:
 *
 * - `today` / `tomorrow` / `yesterday`
 * - `2026-02-03`, `2026/2/3` — year first, always ISO
 * - `3/2/2026`, `3.2.26`, `3-2-2026`, `3 2 2026`
 * - `3/2` — day and month, this year
 * - `3 Feb 2026`, `3rd February 2026`, `Feb 3, 2026`, `3-feb-26`, `3 Feb`
 * - `03022026`, `030226`, `20260203`
 *
 * Returns null for anything else — including a date that doesn't exist.
 */
export function parseDateInput(raw: string, today: Date = new Date()): ParsedDateInput | null {
  const text = raw.trim().toLowerCase();
  if (text.length === 0) return null;

  const offset = RELATIVE_DAYS[text];
  if (offset !== undefined) {
    const date = dayjs(today).startOf('day').add(offset, 'day');
    return { iso: date.format(ISO_FORMAT), date: date.toDate() };
  }

  // Ordinals and commas are punctuation around the numbers, not part of them.
  const cleaned = text
    .replace(/(\d+)(st|nd|rd|th)\b/g, '$1')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Year first is unambiguous by construction — nothing else starts with four digits.
  const yearFirst = /^(\d{4})[-/. ](\d{1,2})[-/. ](\d{1,2})$/.exec(cleaned);
  if (yearFirst) {
    return buildDate(Number(yearFirst[1]), Number(yearFirst[2]), Number(yearFirst[3]));
  }

  const dayMonthYear = /^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{2}|\d{4})$/.exec(cleaned);
  if (dayMonthYear) {
    const { day, month } = orderDayMonth(Number(dayMonthYear[1]), Number(dayMonthYear[2]));
    return buildDate(expandYear(dayMonthYear[3]), month, day);
  }

  // No year typed — this one. The echo spells the year out, so an unintended
  // one is visible before the field commits.
  const dayMonth = /^(\d{1,2})[-/.](\d{1,2})$/.exec(cleaned);
  if (dayMonth) {
    const { day, month } = orderDayMonth(Number(dayMonth[1]), Number(dayMonth[2]));
    return buildDate(today.getFullYear(), month, day);
  }

  if (/^\d{6}$|^\d{8}$/.test(cleaned)) {
    const digits = cleaned;
    if (digits.length === 8) {
      // `20260203` reads as ISO; `03022026` can only be day-first, since 0302
      // is not a year anyone means.
      const asIso = buildDate(
        Number(digits.slice(0, 4)),
        Number(digits.slice(4, 6)),
        Number(digits.slice(6, 8))
      );
      if (asIso) return asIso;
      return buildDate(
        Number(digits.slice(4, 8)),
        Number(digits.slice(2, 4)),
        Number(digits.slice(0, 2))
      );
    }
    return buildDate(
      expandYear(digits.slice(4, 6)),
      Number(digits.slice(2, 4)),
      Number(digits.slice(0, 2))
    );
  }

  // Anything with a written month: the month token can sit anywhere, the
  // numbers around it are the day and (optionally) the year.
  const tokens = cleaned.split(/[\s\-/.]+/).filter(Boolean);
  if (tokens.length < 2 || tokens.length > 3) return null;

  const monthIndex = tokens.findIndex(token => monthFromName(token) !== null);
  if (monthIndex === -1) return null;
  const month = monthFromName(tokens[monthIndex]);
  if (month === null) return null;

  const numbers = tokens.filter((_, index) => index !== monthIndex);
  if (!numbers.every(token => /^\d{1,4}$/.test(token))) return null;

  if (numbers.length === 1) {
    // "Feb 2026" names no day; "3 Feb" means this year.
    if (numbers[0].length === 4) return null;
    return buildDate(today.getFullYear(), month, Number(numbers[0]));
  }

  const [first, second] = numbers;
  // "2026 Feb 3" puts the year first; every other order puts the day there.
  return first.length === 4
    ? buildDate(Number(first), month, Number(second))
    : buildDate(expandYear(second), month, Number(first));
}

/** A trailing `Z` or `±hh:mm` / `±hhmm` offset on an ISO datetime. */
const ZONE_SUFFIX = /(z|[+-]\d{2}:?\d{2})$/i;

/**
 * The UTC calendar day (`"YYYY-MM-DD"`) a date or datetime value falls on —
 * how the backend reads every date filter value. A datetime without a zone is
 * taken as UTC, so `"2030-01-01T23:30:00-02:00"` → `"2030-01-02"`. Null when
 * the value names no day (e.g. a stale uid).
 */
export function utcDayFromValue(value: string): string | null {
  const trimmed = value.trim();
  if (fromIso(trimmed).isValid()) return trimmed;
  const datetime = trimmed.replace(' ', 'T');
  const parsed = new Date(ZONE_SUFFIX.test(datetime) ? datetime : `${datetime}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}
